import { useEffect, useState } from 'react';
import { MapPin, Calendar, Clock, CalendarPlus, Apple } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDateBlock(iso) {
  const d = new Date(`${iso}T12:00:00`);
  return {
    weekday: d.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase(),
    day: d.getDate(),
    month: d.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase(),
  };
}

// Turn "HH:MM" → "HHMM00"
function toIcsTime(t) {
  if (!t || !/^\d{1,2}:\d{2}$/.test(t)) return null;
  const [h, m] = t.split(':');
  return `${h.padStart(2, '0')}${m}00`;
}

// Build "YYYYMMDDTHHMMSS" local strings (no Z, the iCal client treats it as floating local time)
function buildIcsRange(date, start, end) {
  const dateCompact = (date || '').replace(/-/g, '');
  if (!dateCompact) return null;
  const s = toIcsTime(start) || '100000'; // default 10:00 if missing
  const e = toIcsTime(end) || '170000';   // default 17:00 if missing
  return {
    start: `${dateCompact}T${s}`,
    end: `${dateCompact}T${e}`,
  };
}

// Google Calendar "TEMPLATE" link (always opens an "add event" prefilled form)
function googleCalLink(m) {
  const range = buildIcsRange(m.date, m.start_time, m.end_time);
  if (!range) return null;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `${m.name} — Whispering Wristwear`,
    dates: `${range.start}/${range.end}`,
    details: m.notes
      ? `${m.notes}\n\nVisit Lindy at ${m.name}. Try the bracelets on in person.`
      : `Visit Lindy at ${m.name}. Try the bracelets on in person.`,
    location: m.location || '',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// Inline-generated .ics file (Apple Calendar, Outlook, etc.)
function downloadIcs(m) {
  const range = buildIcsRange(m.date, m.start_time, m.end_time);
  if (!range) return;
  const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d+/, '');
  const uid = `${m.id || range.start}@whisperingwristwear.com`;
  const escape = (s) => String(s || '').replace(/[\\,;]/g, (c) => `\\${c}`).replace(/\n/g, '\\n');
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Whispering Wristwear//Markets//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${range.start}`,
    `DTEND:${range.end}`,
    `SUMMARY:${escape(`${m.name} — Whispering Wristwear`)}`,
    `DESCRIPTION:${escape(m.notes ? `${m.notes}\nVisit Lindy in person.` : 'Visit Lindy in person.')}`,
    `LOCATION:${escape(m.location)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safeName = (m.name || 'market').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  a.href = url;
  a.download = `${safeName}-${m.date}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function MarketsCalendar() {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/markets`, { params: { upcoming_only: true } })
      .then((r) => {
        if (!cancelled) setMarkets(r.data || []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || markets.length === 0) return null;

  return (
    <section
      className="section-padding bg-[#F5F5F0]"
      data-testid="markets-calendar"
    >
      <div className="container-custom px-6 md:px-12 lg:px-24">
        <div className="text-center mb-12">
          <p className="text-caption text-[#84A98C] mb-4">Find Me in Person</p>
          <h2 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-4">
            Upcoming Markets
          </h2>
          <p className="text-[#52796F] max-w-xl mx-auto">
            Come say hello, try the bracelets on, and pick your favourite in
            person. Around Paphos, Cyprus.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {markets.slice(0, 6).map((m) => {
            const block = formatDateBlock(m.date);
            return (
              <article
                key={m.id}
                className="bg-[#FAF9F6] p-6 rounded-sm shadow-soft flex gap-5 items-start"
                data-testid={`market-${m.id}`}
              >
                {/* Date block */}
                <div className="shrink-0 text-center bg-[#354F52] text-[#FAF9F6] rounded-sm px-3 py-2 min-w-[64px]">
                  <p className="text-[10px] tracking-widest text-[#D4AF37]">{block.weekday}</p>
                  <p className="font-serif text-3xl leading-tight">{block.day}</p>
                  <p className="text-[10px] tracking-widest text-[#CAD2C5]">{block.month}</p>
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-lg text-[#354F52] leading-snug mb-1">
                    {m.name}
                  </h3>
                  <p className="text-sm text-[#52796F] flex items-center gap-1.5 mb-2">
                    <MapPin className="w-3.5 h-3.5 text-[#84A98C]" strokeWidth={1.5} />
                    {m.location}
                  </p>
                  {(m.start_time || m.end_time) && (
                    <p className="text-xs text-[#84A98C] flex items-center gap-1.5">
                      <Clock className="w-3 h-3" strokeWidth={1.5} />
                      {m.start_time || ''}
                      {m.start_time && m.end_time ? ' – ' : ''}
                      {m.end_time || ''}
                    </p>
                  )}
                  {m.notes && (
                    <p className="text-xs text-[#52796F] mt-2 italic">
                      {m.notes}
                    </p>
                  )}

                  {/* Add to Calendar */}
                  <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-[#CAD2C5]/60" data-testid={`market-${m.id}-cal-links`}>
                    {googleCalLink(m) && (
                      <a
                        href={googleCalLink(m)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#354F52] hover:text-[#D4AF37] transition-colors"
                        data-testid={`market-${m.id}-google`}
                      >
                        <CalendarPlus className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Google
                      </a>
                    )}
                    {buildIcsRange(m.date, m.start_time, m.end_time) && (
                      <>
                        <span className="text-[#CAD2C5]">·</span>
                        <button
                          type="button"
                          onClick={() => downloadIcs(m)}
                          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#354F52] hover:text-[#D4AF37] transition-colors"
                          data-testid={`market-${m.id}-apple`}
                        >
                          <Apple className="w-3.5 h-3.5" strokeWidth={1.5} />
                          Apple / .ics
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div className="text-center mt-8 text-xs text-[#84A98C] flex items-center justify-center gap-2">
          <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
          Showing the next {Math.min(markets.length, 6)} of {markets.length} upcoming markets
        </div>
      </div>
    </section>
  );
}
